type AnalyticsParams = Record<
  string,
  string | number | boolean | null | undefined
>;

type TrackOptions = {
  /** Report the event even while an admin is impersonating someone. */
  allowDuringImpersonation?: boolean;
};

declare global {
  interface Window {
    dataLayer?: IArguments[];
    gtag?: (...args: unknown[]) => void;
  }
}

let gaMeasurementId = (import.meta.env.VITE_GA_MEASUREMENT_ID ?? "").trim();
let adsConversionId = (
  import.meta.env.VITE_GOOGLE_ADS_CONVERSION_ID ?? ""
).trim();
let adsLeadConversionLabel = (
  import.meta.env.VITE_GOOGLE_ADS_LEAD_CONVERSION_LABEL ?? ""
).trim();
// Actions taken by an admin impersonating a user are not that user's behaviour:
// they must not land in the reports, nor in Google Ads as conversions once
// imported. Kept in sync by setAnalyticsUser.
let isImpersonating = false;
let hasInitialized = false;
let hasBoundAutoEvents = false;
let activeViewStartedAt = 0;
let hasScheduledScriptLoad = false;
let hasRequestedScriptLoad = false;
let pendingIdleCallbackId: number | null = null;
let cleanupInteractionListeners: (() => void) | null = null;

const SCRIPT_DATA_ATTR = "data-fs-ga-id";
const SESSION_STORAGE_KEY = "fs-ga-session-id";
const DOWNLOAD_EXTENSIONS = new Set([
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "ppt",
  "pptx",
  "csv",
  "txt",
  "zip",
  "rar",
  "7z",
  "mp4",
  "mp3",
]);

const isBrowser = () =>
  typeof window !== "undefined" && typeof document !== "undefined";

const isAnalyticsEnabled = () => isBrowser() && Boolean(gaMeasurementId);

// Google Ads peut être configuré seul : gtag doit alors être chargé depuis
// l'identifiant AW-. Ce garde ne concerne que le chargement du conteneur de
// tag — les events GA restent gardés par isAnalyticsEnabled().
const hasTagContainer = () =>
  isBrowser() && Boolean(gaMeasurementId || adsConversionId);

const getTagContainerId = () => gaMeasurementId || adsConversionId;

const INTERACTION_EVENTS: ReadonlyArray<keyof WindowEventMap> = [
  "pointerdown",
  "keydown",
  "touchstart",
  "scroll",
];

const sanitizeParams = (params: AnalyticsParams = {}) => {
  const cleaned: Record<string, string | number | boolean | null> = {};

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) cleaned[key] = value;
  }

  return cleaned;
};

const getSessionId = () => {
  if (!isBrowser()) return "";

  const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);

  if (existing) return existing;

  const created =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `s-${Date.now()}-${Math.random().toString(16).slice(2)}`;

  window.sessionStorage.setItem(SESSION_STORAGE_KEY, created);

  return created;
};

const getBaseContext = (): AnalyticsParams => {
  if (!isBrowser()) return {};

  return sanitizeParams({
    page_path: `${window.location.pathname}${window.location.search}`,
    page_title: document.title || undefined,
    language: navigator.language || undefined,
    screen_resolution:
      window.screen?.width && window.screen?.height
        ? `${window.screen.width}x${window.screen.height}`
        : undefined,
    viewport_width: window.innerWidth || undefined,
    viewport_height: window.innerHeight || undefined,
    fs_session_id: getSessionId() || undefined,
  });
};

const getAnchorFromEventTarget = (
  target: EventTarget | null,
): HTMLAnchorElement | null => {
  if (!(target instanceof Element)) return null;

  return target.closest("a[href]");
};

const getFileExtension = (pathname: string) => {
  const segment = pathname.split("/").pop() || "";
  const dot = segment.lastIndexOf(".");

  if (dot <= 0) return "";

  return segment.slice(dot + 1).toLowerCase();
};

const trackEngagementIfNeeded = () => {
  if (!isAnalyticsEnabled() || isImpersonating) return;
  if (!window.gtag || !activeViewStartedAt) return;
  const elapsed = Date.now() - activeViewStartedAt;

  if (elapsed < 1000) return;

  window.gtag(
    "event",
    "user_engagement",
    sanitizeParams({
      ...getBaseContext(),
      engagement_time_msec: elapsed,
    }),
  );
  activeViewStartedAt = Date.now();
};

const bindAutoEvents = () => {
  if (!isBrowser() || hasBoundAutoEvents) return;

  document.addEventListener(
    "click",
    (event) => {
      const anchor = getAnchorFromEventTarget(event.target);

      if (!anchor || !anchor.href || !window.gtag || isImpersonating) return;

      let url: URL;

      try {
        url = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }

      const isOutbound = url.host !== window.location.host;

      if (isOutbound) {
        window.gtag(
          "event",
          "click",
          sanitizeParams({
            ...getBaseContext(),
            event_category: "navigation",
            event_label: url.href,
            outbound: true,
            link_domain: url.host,
          }),
        );
      }

      const extension = getFileExtension(url.pathname);

      if (extension && DOWNLOAD_EXTENSIONS.has(extension)) {
        window.gtag(
          "event",
          "file_download",
          sanitizeParams({
            ...getBaseContext(),
            file_name: url.pathname.split("/").pop() || undefined,
            file_extension: extension,
            link_url: url.href,
          }),
        );
      }
    },
    { capture: true },
  );

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      trackEngagementIfNeeded();

      return;
    }
    activeViewStartedAt = Date.now();
  });

  window.addEventListener("beforeunload", () => {
    if (!window.gtag || isImpersonating) return;
    trackEngagementIfNeeded();
    window.gtag(
      "event",
      "session_end",
      sanitizeParams({
        ...getBaseContext(),
        active_view_duration_seconds: Math.round(
          (Date.now() - activeViewStartedAt) / 1000,
        ),
      }),
    );
  });

  activeViewStartedAt = Date.now();
  hasBoundAutoEvents = true;
};

const loadAnalyticsScript = () => {
  if (!hasTagContainer() || hasRequestedScriptLoad) return;
  hasRequestedScriptLoad = true;

  const containerId = getTagContainerId();
  const selector = `script[${SCRIPT_DATA_ATTR}="${containerId}"]`;
  const existing = document.querySelector(selector);

  if (existing) return;

  const script = document.createElement("script");

  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(containerId)}`;
  script.setAttribute(SCRIPT_DATA_ATTR, containerId);
  document.head.appendChild(script);
};

const scheduleAnalyticsScriptLoad = () => {
  if (!hasTagContainer() || hasScheduledScriptLoad) return;
  hasScheduledScriptLoad = true;

  const triggerImmediateLoad = () => {
    if (cleanupInteractionListeners) {
      cleanupInteractionListeners();
      cleanupInteractionListeners = null;
    }
    if (pendingIdleCallbackId != null && "cancelIdleCallback" in window) {
      (window as any).cancelIdleCallback(pendingIdleCallbackId);
      pendingIdleCallbackId = null;
    }
    loadAnalyticsScript();
  };

  cleanupInteractionListeners = () => {
    INTERACTION_EVENTS.forEach((eventName) => {
      window.removeEventListener(eventName, triggerImmediateLoad, true);
    });
  };
  INTERACTION_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, triggerImmediateLoad, {
      capture: true,
      once: true,
      passive: true,
    });
  });

  if ("requestIdleCallback" in window) {
    pendingIdleCallbackId = (window as any).requestIdleCallback(
      () => {
        pendingIdleCallbackId = null;
        triggerImmediateLoad();
      },
      { timeout: 3000 },
    );

    return;
  }

  globalThis.setTimeout(triggerImmediateLoad, 1500);
};

const ensureGtag = () => {
  if (!hasTagContainer()) return false;

  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtagShim() {
      window.dataLayer?.push(arguments);
    };
  }
  scheduleAnalyticsScriptLoad();

  return true;
};

export const setAnalyticsMeasurementId = (measurementId?: string | null) => {
  const nextMeasurementId = String(measurementId || "").trim();

  if (nextMeasurementId !== gaMeasurementId) {
    hasInitialized = false;
    hasScheduledScriptLoad = false;
    hasRequestedScriptLoad = false;
    pendingIdleCallbackId = null;
    cleanupInteractionListeners?.();
    cleanupInteractionListeners = null;
  }
  gaMeasurementId = nextMeasurementId;
};

export const setAdsConversionConfig = (config?: {
  conversionId?: string | null;
  leadLabel?: string | null;
}) => {
  const nextConversionId = String(config?.conversionId || "").trim();

  if (nextConversionId !== adsConversionId) {
    hasInitialized = false;
    hasScheduledScriptLoad = false;
    hasRequestedScriptLoad = false;
    pendingIdleCallbackId = null;
    cleanupInteractionListeners?.();
    cleanupInteractionListeners = null;
  }
  adsConversionId = nextConversionId;
  adsLeadConversionLabel = String(config?.leadLabel || "").trim();
};

export const initAnalytics = () => {
  if (!ensureGtag() || hasInitialized || !window.gtag) return;

  window.gtag("js", new Date());
  if (gaMeasurementId) {
    window.gtag("config", gaMeasurementId, {
      send_page_view: false,
    });
  }
  if (adsConversionId) {
    window.gtag("config", adsConversionId);
  }

  if (gaMeasurementId) bindAutoEvents();
  hasInitialized = true;
};

// Conversion Google Ads. Indépendante de GA : l'une ou l'autre peut être
// configurée seule. À appeler une seule fois, depuis le handler de soumission
// (jamais dans un effet : un re-render doublerait la conversion).
export const trackAdsConversion = (
  label?: string,
  params: AnalyticsParams = {},
) => {
  if (!isBrowser() || isImpersonating) return;

  const sendToLabel = String(label || adsLeadConversionLabel).trim();

  if (!adsConversionId || !sendToLabel) return;
  if (!ensureGtag() || !window.gtag) return;

  initAnalytics();
  if (!window.gtag) return;

  window.gtag("event", "conversion", {
    ...sanitizeParams(params),
    send_to: `${adsConversionId}/${sendToLabel}`,
  });
};

export const trackPageView = (path: string) => {
  if (!isAnalyticsEnabled() || isImpersonating) return;

  initAnalytics();
  if (!window.gtag) return;

  const pagePath = String(
    path || `${window.location.pathname}${window.location.search}`,
  );

  window.gtag("event", "page_view", {
    ...getBaseContext(),
    page_path: pagePath,
    page_location: `${window.location.origin}${pagePath}`,
    page_referrer: document.referrer || undefined,
  });
  activeViewStartedAt = Date.now();
};

export const trackAnalyticsEvent = (
  eventName: string,
  params: AnalyticsParams = {},
  options: TrackOptions = {},
) => {
  if (!isAnalyticsEnabled()) return;
  if (isImpersonating && !options.allowDuringImpersonation) return;

  const name = String(eventName || "").trim();

  if (!name) return;

  initAnalytics();
  if (!window.gtag) return;

  window.gtag(
    "event",
    name,
    sanitizeParams({
      ...getBaseContext(),
      ...params,
    }),
  );
};

export const setAnalyticsUser = (user: {
  id?: string;
  role?: string;
  admin?: boolean;
  emailVerified?: boolean;
  impersonationActive?: boolean;
  isAuthenticated?: boolean;
}) => {
  isImpersonating = Boolean(user.impersonationActive);

  if (!isAnalyticsEnabled() || isImpersonating) return;

  initAnalytics();
  if (!window.gtag) return;

  const userId = user.id ? String(user.id) : null;

  window.gtag("set", { user_id: userId });

  window.gtag(
    "set",
    "user_properties",
    sanitizeParams({
      role: user.role,
      admin: user.admin,
      email_verified: user.emailVerified,
      impersonation_active: user.impersonationActive,
      is_authenticated: user.isAuthenticated,
    }),
  );
};
